import { isSupportedOrganizationLocale } from '~/components/CmsMain/localeGlobals';
import useDataFetcher from '~/components/useDataFetcher';
import type { SelectableLocalesResponse } from '~/server_shared/openapi/data-contracts';

import type { SelectableLocale } from './types';

export interface SelectableLocalesState {
  locales: SelectableLocale[];
  isLoading: boolean;
  isError: boolean;
}

/**
 * The locales this organization can be configured with.
 *
 * Two halves, each computed where its data lives. The server enumerates the territories `holidays`
 * covers and the language CLDR gives each of them — offering a locale whose territory has no holiday
 * calendar would 500 every business-day evaluation for the organization. `isSupportedOrganizationLocale`
 * adds the two capabilities that only frontend packages know about: an `iso-country-currency` mapping
 * for the region (without one the organization has no currency to format money with, and
 * `Intl.NumberFormat` with `style: 'currency'` requires one) and moment locale data (the absence of
 * which silently keeps English date formats).
 *
 * No hardcoded list on either side, so neither half can drift when `holidays`, CLDR, moment or
 * `iso-country-currency` is upgraded.
 */
export function useSelectableLocales(organizationId: number): SelectableLocalesState {
  const { data, isLoading, isError } = useDataFetcher<SelectableLocalesResponse>(
    `/sysconfig/api/v1/organizations/${organizationId}/locales`
  );

  return {
    locales: (data?.locales || []).filter(({ code }) => isSupportedOrganizationLocale(code)),
    isLoading,
    isError,
  };
}
